import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Signal } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { forkJoin, Observable, switchMap } from 'rxjs';
import { BaseDatatableStore, IBaseDatatableState } from '../../../../state/base-datatable-store.service';
import { ELoadStatus } from '../../../../state/state.interface';
import {
  HttpUtilitiesService,
  IBaseCrudResponse,
  IBaseResponse,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../../utilities/http-utilities.service';
import { IAiAgent, IEditAiAgent, TAddAiAgent, TAiProviderConfigs, TAiProviderOption } from './ai-agents.interface';

export interface IAiAgentComponentState extends IBaseDatatableState<IAiAgent> {
  bulkCrudLoading: ELoadStatus;
  bulkOperationFailedData: IBaseCrudResponse[];
  data: IAiAgent[];
  dataLoading: ELoadStatus;
  dataTotal: number;
  providerConfigs: TAiProviderConfigs | null;
  providers: TAiProviderOption[];
  singleCrudLoading: ELoadStatus;
}

@Injectable()
export class AiAgentsStore extends BaseDatatableStore<IAiAgent, IAiAgentComponentState> {
  readonly providers$: Signal<TAiProviderOption[]> = this.selectSignal(
    (state: IAiAgentComponentState) => state.providers,
  );
  readonly providerConfigs$: Signal<TAiProviderConfigs | null> = this.selectSignal(
    (state: IAiAgentComponentState) => state.providerConfigs,
  );

  readonly createOne = this.effect((trigger$: Observable<TAddAiAgent>) =>
    trigger$.pipe(
      switchMap((item: TAddAiAgent) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.createItem(item).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly updateOne = this.effect((trigger$: Observable<IEditAiAgent>) =>
    trigger$.pipe(
      switchMap((item: IEditAiAgent) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.updateItem(item).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  override readonly deleteOne = this.effect((trigger$: Observable<number>) =>
    trigger$.pipe(
      switchMap((id: number) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.deleteItem(id).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  override readonly deleteBulk = this.effect((trigger$: Observable<number[]>) =>
    trigger$.pipe(
      switchMap((itemIds: number[]) => {
        this.patchState({ bulkCrudLoading: ELoadStatus.loading });

        return this.deleteItems(itemIds).pipe(
          tapResponse(
            (response) =>
              this.patchState({ bulkCrudLoading: ELoadStatus.success, bulkOperationFailedData: response.data }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadItems = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ dataLoading: ELoadStatus.loading });

        return this.getItems(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IAiAgent>) =>
              this.patchState({ data: response.data, dataLoading: ELoadStatus.success, dataTotal: response.total }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  // Providers (for the dropdown and the table's provider names) and their model lists.
  readonly loadProviders = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      switchMap(() =>
        forkJoin({ configs: this.getProviderConfigs(), providers: this.getProviders() }).pipe(
          tapResponse(
            ({ configs, providers }) => this.patchState({ providerConfigs: configs.data, providers: providers.data }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        ),
      ),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {
    super({
      bulkCrudLoading: ELoadStatus.initial,
      bulkOperationFailedData: [],
      data: [],
      dataLoading: ELoadStatus.initial,
      dataTotal: 0,
      providerConfigs: null,
      providers: [],
      singleCrudLoading: ELoadStatus.initial,
    });
  }

  private createItem(body: TAddAiAgent): Observable<IBaseResponse<IAiAgent>> {
    return this.http.post<IBaseResponse<IAiAgent>>('ai-agents', body);
  }

  private updateItem(item: IEditAiAgent): Observable<IBaseResponse<IAiAgent>> {
    return this.http.patch<IBaseResponse<IAiAgent>>(`ai-agents/${item.id}`, item.dto);
  }

  private deleteItem(id: number): Observable<IBaseResponse<void>> {
    return this.http.delete<IBaseResponse<void>>(`ai-agents/${id}`);
  }

  private getItems(params: HttpParams): Observable<IGetManyResponse<IAiAgent>> {
    return this.http.get<IGetManyResponse<IAiAgent>>('ai-agents', { params });
  }

  private deleteItems(ids: number[]): Observable<IBaseResponse<IBaseCrudResponse[]>> {
    return this.http.delete<IBaseResponse<IBaseCrudResponse[]>>('ai-agents/bulk/delete', {
      body: { payload: ids },
    });
  }

  private getProviders(): Observable<IGetManyResponse<TAiProviderOption>> {
    const params: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters({
      fields: ['name', 'providerType'],
      limit: 1000,
      sort: [{ active: 'name', direction: 'asc' }],
    });

    return this.http.get<IGetManyResponse<TAiProviderOption>>('ai-providers', { params });
  }

  private getProviderConfigs(): Observable<IBaseResponse<TAiProviderConfigs>> {
    return this.http.get<IBaseResponse<TAiProviderConfigs>>('ai-providers/configs');
  }
}
